import { Apple, ArrowRight, Bot, Play, UserRound, Users, Video } from "lucide-react";
import { Link } from "react-router-dom";
import coverImage from "../../../../brand/topzle-cover.webp";
import heroImage from "../../../../brand/topzle-hero.webp";
import { trackEvent } from "../analytics";
import { guestSetupPath, signInPath } from "../authReturn";
import { gameGuideById } from "../gameGuides";
import { gameLaunchPath } from "../gameLaunchIntent";
import { roomGameCatalog } from "../roomGameCatalog";
import { useAppStore } from "../state";
import { useAuthSession } from "../useAuthSession";

export function LandingPage() {
  const { session } = useAuthSession();
  const guest = useAppStore((state) => state.guest);
  const accountDestination = session ? "/app" : "/sign-in";
  const accountAction = session ? "Open dashboard" : "Sign in";
  const guestDestination = guest ? "/app" : guestSetupPath("/app");
  const guestAction = guest ? "Continue as guest" : "Play anonymously";
  return (
    <>
      <section className="hero">
        <img className="hero-brand-image" src={heroImage} alt="" aria-hidden="true" />
        <div className="hero-copy">
          <span className="eyebrow">
            <span className="pulse" /> PLAY · TALK · REPEAT
          </span>
          <h1>
            Game night,
            <br />
            <em>made easy.</em>
          </h1>
          <p>Pick a game, share one link, and play face-to-face with your favourite people.</p>
          <div className="hero-actions">
            <Link className="button button-primary" to={accountDestination}>
              {accountAction} <ArrowRight size={18} />
            </Link>
            {!session && (
              <Link
                className="button button-ghost"
                to={guestDestination}
                onClick={() => trackEvent("guest_play_started", { source: "homepage_hero" })}
              >
                <UserRound size={18} /> {guestAction}
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="simple-home-games">
        <div className="simple-home-inner">
          <div className="simple-section-head">
            <div>
              <span className="eyebrow">PLAYABLE NOW</span>
              <h2>Pick a game.</h2>
            </div>
            <Link className="text-link" to="/games">
              See games <ArrowRight />
            </Link>
          </div>
          <div className="simple-games">
            {roomGameCatalog.map((game, index) => {
              const guide = gameGuideById.get(game.id);
              const launchPath = gameLaunchPath(game.id);
              const playDestination = session || guest ? launchPath : signInPath(launchPath);
              return (
                <article className={`simple-game-card simple-game-${index + 1}`} key={game.id}>
                  <span className="simple-game-symbol">{guide?.symbol}</span>
                  <h3>{game.name}</h3>
                  <div>
                    <Link
                      to={playDestination}
                      onClick={() =>
                        trackEvent("select_content", {
                          content_type: "game",
                          item_id: game.id,
                          source: "homepage_game_card",
                        })
                      }
                    >
                      Play <ArrowRight />
                    </Link>
                    <Link
                      to={`/how-to-play/${game.id}`}
                      onClick={() =>
                        trackEvent("tutorial_opened", {
                          game_id: game.id,
                          source: "homepage_game_card",
                        })
                      }
                    >
                      Guide
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="simple-benefits">
        <article>
          <Users />
          <div>
            <h2>Play together</h2>
            <p>Switch games without leaving the room.</p>
          </div>
        </article>
        <article>
          <Bot />
          <div>
            <h2>Start with a bot</h2>
            <p>Play now. Invite friends later.</p>
          </div>
        </article>
        <article>
          <Video />
          <div>
            <h2>Talk your way</h2>
            <p>Camera and mic start with the room, with controls always close by.</p>
          </div>
        </article>
      </section>

      <section className="store-coming-soon" aria-labelledby="store-coming-soon-title">
        <div>
          <span className="eyebrow">TOPZLE ON THE GO</span>
          <h2 id="store-coming-soon-title">Game night is coming to your phone.</h2>
          <p>
            Native Topzle apps are on the way. For now, the full experience is ready in your mobile
            browser.
          </p>
        </div>
        <div className="store-coming-soon-badges">
          <div className="store-coming-soon-badge">
            <Play aria-hidden="true" />
            <span>
              <small>Coming soon to</small>
              Google Play
            </span>
          </div>
          <div className="store-coming-soon-badge">
            <Apple aria-hidden="true" />
            <span>
              <small>Coming soon to the</small>
              App Store
            </span>
          </div>
        </div>
      </section>

      <section className="brand-cover-section" aria-label="Topzle: Play. Talk. Repeat.">
        <img src={coverImage} alt="Topzle — Play. Talk. Repeat." />
      </section>

      <section className="simple-home-cta">
        <div>
          <span className="eyebrow">YOUR TURN</span>
          <h2>Ready to play?</h2>
          <p>Open a room. Send the link. Have fun.</p>
        </div>
        <div className="simple-home-cta-actions">
          <Link className="button simple-light-button" to={accountDestination}>
            {accountAction} <ArrowRight />
          </Link>
          {!session && (
            <Link
              className="button button-ghost"
              to={guestDestination}
              onClick={() => trackEvent("guest_play_started", { source: "homepage_footer" })}
            >
              <UserRound size={18} /> {guestAction}
            </Link>
          )}
        </div>
      </section>
    </>
  );
}
